import { useMemo, useState } from 'react';
import { Link, useSearchParams } from 'react-router';
import { tx } from '../i18n';
import { useConsulta } from '../lib/consulta';
import { hace, miles } from '../lib/formato';
import type { EquipoDeRed, Organizacion, RedDeAppliance } from '../tipos';
import { Cabecera, Consulta, Pastilla, Tabla, Vacio } from '../ui/piezas';

/** Una foto de hace más de esto ya no cuenta como "ahora": se avisa en la pantalla. */
const FOTO_VIEJA_MS = 3 * 60_000;
const MIN = 60_000;
const HORA = 60 * MIN;
/** Los tramos de antigüedad de una caída (en ms, el borde de arriba de cada uno). */
const TRAMOS = [15 * MIN, HORA, 6 * HORA, 24 * HORA, 72 * HORA, Infinity];

type Estado = 'caido' | 'parcial' | 'arriba' | 'desconocido';
const TONO: Record<Estado, 'caido' | 'parcial' | 'arriba' | 'desconocido'> = {
  caido: 'caido',
  parcial: 'parcial',
  arriba: 'arriba',
  desconocido: 'desconocido',
};

/** Desde cuándo está mal: el inicio de su caída, o el de su estado. */
const inicioDe = (e: EquipoDeRed) => Date.parse(e.caida?.inicio ?? e.desde ?? '') || null;
const mal = (e: EquipoDeRed) => e.estado === 'caido' || e.estado === 'parcial';
const tramoDe = (ms: number) => TRAMOS.findIndex((t) => ms < t);

/**
 * La red de todos los clientes que **autorizaron** compartirla (decisión 19), como tablero del
 * NOC: arriba cuánto está bien y cuánto no, en el medio qué cliente está peor, desde cuándo y
 * qué está fallando —todo se puede tocar para filtrar— y abajo los equipos en una sola lista,
 * una línea por equipo, los peores primero, con su propio scroll (la página no crece sin fin).
 *
 * Lo que se ve es la última foto que mandó cada appliance. Si tiene rato, se dice: el NOC no
 * puede confundir "está arriba" con "estaba arriba hace veinte minutos".
 */
export function Red() {
  const [params, setParams] = useSearchParams();
  const organizacion = params.get('organizacion') ?? '';
  const estado = params.get('estado') ?? 'caidos';
  const q = (params.get('q') ?? '').toLowerCase();
  const cliente = params.get('cliente') ?? '';
  const tramo = params.get('tramo') ? Number(params.get('tramo')) : null;
  const prueba = params.get('prueba') ?? '';

  // todo el parque de una vez (los gráficos lo necesitan entero); el filtro es acá
  const consulta = new URLSearchParams({ estado: 'todos' });
  if (organizacion) consulta.set('organizacion', organizacion);
  const redes = useConsulta<RedDeAppliance[]>('/red', { refetchInterval: 30_000 });
  const todos = useConsulta<EquipoDeRed[]>(`/red/equipos?${consulta.toString()}`, { refetchInterval: 30_000 });
  const organizaciones = useConsulta<Organizacion[]>('/organizaciones');

  const poner = (cambios: Record<string, string | null>) => {
    const p = new URLSearchParams(params);
    for (const [k, v] of Object.entries(cambios)) {
      if (v) p.set(k, v);
      else p.delete(k);
    }
    setParams(p, { replace: true });
  };

  // "ahora" es cuándo llegaron los datos (no un Date.now() en cada render, que además
  // anulaba el memo): lo que se dice "hace X" se mide contra esa lectura
  const [arranque] = useState(() => Date.now());
  const ahora = todos.dataUpdatedAt || arranque;
  const equipos = useMemo(() => todos.data ?? [], [todos.data]);
  const d = useMemo(() => resumir(equipos, ahora), [equipos, ahora]);

  // lo que se muestra abajo: estado, búsqueda y lo que se tocó en los gráficos
  const visibles = equipos.filter((e) => {
    if (estado === 'caidos' ? !mal(e) : estado !== 'todos' && e.estado !== estado) return false;
    if (q && !`${e.nombre} ${e.direccion}`.toLowerCase().includes(q)) return false;
    if (cliente && (e.cliente ?? '—') !== cliente) return false;
    if (tramo !== null) {
      const i = inicioDe(e);
      if (!mal(e) || !i || tramoDe(ahora - i) !== tramo) return false;
    }
    if (prueba && !e.caida?.pruebas_caidas?.includes(prueba)) return false;
    return true;
  });
  const lista = ordenar(visibles, ahora);
  // una columna que dice lo mismo en todas las filas no dice nada: se esconde
  const variosClientes = new Set(lista.map((e) => e.cliente ?? '—')).size > 1;
  const variosAppliances = new Set(lista.map((e) => e.appliance_id)).size > 1;
  const filtros = [
    cliente && { k: 'cliente', texto: cliente },
    tramo !== null && { k: 'tramo', texto: tx.red.tramos[tramo] },
    prueba && { k: 'prueba', texto: prueba },
  ].filter(Boolean) as { k: string; texto: string }[];

  return (
    <div className="pagina red">
      <Cabecera titulo={tx.red.titulo}>{tx.red.que}</Cabecera>

      <Consulta q={redes} vacio={<Vacio>{tx.red.sinConsentimiento}</Vacio>}>
        {/* los indicadores y de dónde sale cada foto */}
        <section className="red-kpis">
          <Kpi
            titulo={tx.red.kpi.disponibilidad}
            valor={d.medibles ? `${((d.arriba / d.medibles) * 100).toFixed(1)} %` : '—'}
            detalle={tx.red.kpi.disponibilidadQue(miles(d.arriba), miles(d.medibles))}
            tono={d.medibles && d.arriba / d.medibles < 0.9 ? 'caido' : d.medibles && d.arriba / d.medibles < 0.97 ? 'parcial' : 'arriba'}
            barra={d.medibles ? d.arriba / d.medibles : 0}
            alTocar={() => poner({ estado: 'todos', cliente: null, tramo: null, prueba: null })}
          />
          <Kpi
            titulo={tx.red.kpi.caidos}
            valor={miles(d.caidos)}
            tono={d.caidos ? 'caido' : 'arriba'}
            alTocar={() => poner({ estado: 'caidos', tramo: null })}
          />
          <Kpi
            titulo={tx.red.kpi.parciales}
            valor={miles(d.parciales)}
            tono={d.parciales ? 'parcial' : 'arriba'}
            alTocar={() => poner({ estado: 'caidos' })}
          />
          <Kpi
            titulo={tx.red.kpi.sinDatos}
            valor={miles(d.desconocidos)}
            tono="desconocido"
            alTocar={() => poner({ estado: 'desconocido', tramo: null })}
          />
          <Kpi
            titulo={tx.red.kpi.nuevas}
            valor={miles(d.nuevas)}
            detalle={tx.red.kpi.nuevasQue}
            tono={d.nuevas ? 'caido' : 'arriba'}
            alTocar={() => poner({ estado: 'caidos', tramo: null })}
          />
          <Kpi
            titulo={tx.red.kpi.cronicas}
            valor={miles(d.cronicas)}
            detalle={tx.red.kpi.cronicasQue}
            tono={d.cronicas ? 'parcial' : 'arriba'}
            alTocar={() => poner({ estado: 'caidos', tramo: null })}
          />
          <div className="red-fuentes">
            <small className="red-rotulo">{tx.red.fuentes}</small>
            {(redes.data ?? []).map((r) => {
              const vieja = ahora - Date.parse(r.hora) > FOTO_VIEJA_MS;
              return (
                <div key={r.appliance_id} className="red-fuente">
                  <Link to={`/appliances/${r.appliance_id}`}>{r.organizacion}</Link>
                  <Pastilla tono={r.nivel === 'operar' ? 'parcial' : 'desconocido'}>
                    {r.nivel === 'operar' ? tx.red.verYOperar : tx.red.soloVer}
                  </Pastilla>
                  <small className={vieja ? 'red-vieja' : 'd'}>{vieja ? tx.red.fotoVieja(hace(r.hora)) : tx.red.actualizado(hace(r.hora))}</small>
                </div>
              );
            })}
          </div>
        </section>

        {/* los gráficos: cada barra filtra lo de abajo */}
        <section className="red-graficos">
          <article className="panel red-grafico">
            <h2 className="rotulo">{tx.red.porCliente}</h2>
            <p className="d">{tx.red.porClienteQue}</p>
            <div className="red-barras">
              {d.clientes.map((c) => (
                <button
                  key={c.nombre}
                  type="button"
                  className={`red-barra${cliente === c.nombre ? ' elegida' : ''}`}
                  onClick={() => poner({ cliente: cliente === c.nombre ? null : c.nombre, estado: cliente === c.nombre ? estado : 'todos' })}
                  aria-pressed={cliente === c.nombre}
                >
                  <span className="red-barra-nombre">{c.nombre}</span>
                  <span className="red-apilada" aria-hidden>
                    {(['caido', 'parcial', 'desconocido', 'arriba'] as Estado[]).map((s) =>
                      c[s] ? <i key={s} className={s} style={{ flexGrow: c[s] }} title={`${tx.red.estadoEquipo[s]}: ${c[s]}`} /> : null,
                    )}
                  </span>
                  <span className={`red-barra-cifra${c.caido ? ' caido' : ''}`}>{c.caido ? tx.red.nCaidos(c.caido) : tx.red.todoArriba}</span>
                  <span className="red-barra-total d">{miles(c.total)}</span>
                </button>
              ))}
            </div>
          </article>

          <article className="panel red-grafico">
            <h2 className="rotulo">{tx.red.antiguedad}</h2>
            <p className="d">{tx.red.antiguedadQue}</p>
            {d.caidos + d.parciales === 0 ? (
              <p className="red-nada">{tx.red.nadaQueGraficar}</p>
            ) : (
              <div className="red-histo" role="group" aria-label={tx.red.antiguedad}>
                {d.tramos.map((n, i) => (
                  <button
                    key={i}
                    type="button"
                    className={`red-columna t${i}${tramo === i ? ' elegida' : ''}`}
                    aria-pressed={tramo === i}
                    aria-label={`${tx.red.tramos[i]}: ${tx.red.nEquipos(n)}`}
                    onClick={() => poner({ tramo: tramo === i ? null : String(i), estado: 'caidos' })}
                  >
                    <b>{n || ''}</b>
                    <span className="red-columna-barra" style={{ height: `${d.maxTramo ? Math.max(n ? 6 : 0, (n / d.maxTramo) * 100) : 0}%` }} />
                    <small>{tx.red.tramos[i]}</small>
                  </button>
                ))}
              </div>
            )}
          </article>

          <article className="panel red-grafico">
            <h2 className="rotulo">{tx.red.queFalla}</h2>
            <p className="d">{tx.red.queFallaQue}</p>
            {d.pruebas.length === 0 ? (
              <p className="red-nada">{tx.red.nadaQueGraficar}</p>
            ) : (
              <div className="red-fallas">
                {d.pruebas.map(([p, n]) => (
                  <button
                    key={p}
                    type="button"
                    className={`red-falla${prueba === p ? ' elegida' : ''}`}
                    aria-pressed={prueba === p}
                    onClick={() => poner({ prueba: prueba === p ? null : p, estado: 'caidos' })}
                  >
                    <span className="red-falla-nombre">{p}</span>
                    <span className="red-falla-barra">
                      <i style={{ width: `${(n / d.pruebas[0][1]) * 100}%` }} />
                    </span>
                    <b>{n}</b>
                  </button>
                ))}
              </div>
            )}
          </article>
        </section>
      </Consulta>

      <div className="filtros">
        <input
          className="entrada buscar"
          type="search"
          placeholder={tx.red.buscarEjemplo}
          aria-label={tx.red.buscarEquipos}
          defaultValue={params.get('q') ?? ''}
          onChange={(e) => poner({ q: e.target.value.trim() })}
        />
        <select
          className="lista-sel"
          aria-label={tx.comun.organizacion}
          value={organizacion}
          onChange={(e) => poner({ organizacion: e.target.value })}
        >
          <option value="">{tx.red.todaOrganizacion}</option>
          {(organizaciones.data ?? []).map((o) => (
            <option key={o.id} value={o.id}>
              {o.nombre}
            </option>
          ))}
        </select>
        <div className="seg" role="group" aria-label={tx.comun.estado}>
          {(['caidos', 'todos', 'arriba', 'desconocido'] as const).map((v) => (
            <button key={v} type="button" aria-pressed={estado === v} onClick={() => poner({ estado: v })}>
              {tx.red.estados[v]}
            </button>
          ))}
        </div>
        {filtros.length > 0 && (
          <span className="red-filtros">
            <small className="d">{tx.red.filtrandoPor}</small>
            {filtros.map((f) => (
              <button key={f.k} type="button" className="red-chip" onClick={() => poner({ [f.k]: null })} title={tx.red.quitarFiltro}>
                {f.texto} ✕
              </button>
            ))}
          </span>
        )}
        <span className="filtros-cuenta d">{tx.red.cuenta(`${miles(visibles.length)}/${miles(equipos.length)}`, miles(d.caidos + d.parciales))}</span>
      </div>

      <Consulta
        q={todos}
        vacio={
          visibles.length === 0 ? <Vacio>{estado === 'caidos' && !filtros.length && !q ? tx.red.nadaCaido : tx.red.nadaCoincide}</Vacio> : undefined
        }
      >
        <div className="panel red-lista">
          <Tabla className="red-tabla">
            <thead>
              <tr>
                <th>{tx.red.columnas.equipo}</th>
                <th>{tx.red.columnas.estado}</th>
                <th>{tx.red.columnas.queFalla}</th>
                {variosClientes && <th>{tx.red.columnas.cliente}</th>}
                <th>{tx.red.columnas.direccion}</th>
                {variosAppliances && <th>{tx.red.columnas.appliance}</th>}
              </tr>
            </thead>
            <tbody>
              {lista.map((e) => {
                const i = inicioDe(e);
                const t = mal(e) && i ? tramoDe(ahora - i) : -1;
                return (
                  <tr key={`${e.appliance_id}-${e.id}`} className={`red-fila ${e.estado}`}>
                    <td>
                      <span className="red-nombre">
                        <span className={`red-punto ${TONO[e.estado as Estado] ?? 'desconocido'}`} aria-hidden />
                        <b title={e.nombre}>{e.nombre}</b>
                        {e.modelo && <small className="d">{e.modelo}</small>}
                      </span>
                    </td>
                    <td>
                      {mal(e) && i ? (
                        <span className="red-estado">
                          <span className={`red-edad t${t}`}>{hace(new Date(i).toISOString())}</span>
                          {e.caida?.reconocida && <small className="d">{tx.red.reconocida}</small>}
                        </span>
                      ) : (
                        <span className={`red-estado-txt ${e.estado}`}>{tx.red.estadoEquipo[e.estado] ?? e.estado}</span>
                      )}
                    </td>
                    <td className="d red-pruebas">{e.caida?.pruebas_caidas?.length ? e.caida.pruebas_caidas.join(', ') : ''}</td>
                    {variosClientes && (
                      <td>
                        <button type="button" className="red-cliente" onClick={() => poner({ cliente: e.cliente ?? '—' })}>
                          {e.cliente ?? '—'}
                        </button>
                        {e.sitio && e.sitio !== e.cliente && <small className="d red-sitio"> · {e.sitio}</small>}
                      </td>
                    )}
                    <td className="num d">{e.direccion}</td>
                    {variosAppliances && <td className="d">{e.appliance}</td>}
                  </tr>
                );
              })}
            </tbody>
          </Tabla>
        </div>
      </Consulta>
    </div>
  );
}

function Kpi({
  titulo,
  valor,
  detalle,
  tono,
  barra,
  alTocar,
}: {
  titulo: string;
  valor: string;
  detalle?: string;
  tono: 'caido' | 'parcial' | 'arriba' | 'desconocido';
  barra?: number;
  alTocar: () => void;
}) {
  return (
    <button type="button" className={`red-kpi ${tono}`} onClick={alTocar}>
      <small className="red-rotulo">{titulo}</small>
      <b>{valor}</b>
      {barra !== undefined && (
        <span className="red-kpi-barra" aria-hidden>
          <i style={{ width: `${barra * 100}%` }} />
        </span>
      )}
      {detalle && <small className="d">{detalle}</small>}
    </button>
  );
}

/** Todo lo que muestran los indicadores y los gráficos, del parque entero (sin los filtros de abajo). */
function resumir(equipos: EquipoDeRed[], ahora: number) {
  const cuenta = { caido: 0, parcial: 0, arriba: 0, desconocido: 0 } as Record<Estado, number>;
  const porCliente = new Map<string, Record<Estado, number> & { total: number }>();
  const tramos = TRAMOS.map(() => 0);
  const pruebas = new Map<string, number>();
  let nuevas = 0;
  let cronicas = 0;
  for (const e of equipos) {
    const s = (e.estado in cuenta ? e.estado : 'desconocido') as Estado;
    cuenta[s]++;
    const nombre = e.cliente ?? '—';
    const c = porCliente.get(nombre) ?? { caido: 0, parcial: 0, arriba: 0, desconocido: 0, total: 0 };
    c[s]++;
    c.total++;
    porCliente.set(nombre, c);
    if (mal(e)) {
      const i = inicioDe(e);
      if (i) {
        const edad = ahora - i;
        tramos[tramoDe(edad)]++;
        if (e.estado === 'caido' && edad < HORA) nuevas++;
        if (e.estado === 'caido' && edad >= 24 * HORA) cronicas++;
      }
      for (const p of e.caida?.pruebas_caidas ?? []) pruebas.set(p, (pruebas.get(p) ?? 0) + 1);
    }
  }
  return {
    caidos: cuenta.caido,
    parciales: cuenta.parcial,
    arriba: cuenta.arriba,
    desconocidos: cuenta.desconocido,
    medibles: cuenta.caido + cuenta.parcial + cuenta.arriba,
    nuevas,
    cronicas,
    tramos,
    maxTramo: Math.max(...tramos),
    // los que están peor arriba
    clientes: [...porCliente.entries()]
      .map(([nombre, c]) => ({ nombre, ...c }))
      .sort((a, b) => b.caido - a.caido || b.parcial - a.parcial || b.total - a.total || a.nombre.localeCompare(b.nombre)),
    pruebas: [...pruebas.entries()].sort((a, b) => b[1] - a[1]).slice(0, 8),
  };
}

/** Caídos antes que parciales, los que llevan más tiempo primero; después por cliente y nombre. */
function ordenar(equipos: EquipoDeRed[], ahora: number) {
  const orden: Record<string, number> = { caido: 0, parcial: 1, desconocido: 2, arriba: 3 };
  const edad = (e: EquipoDeRed) => ahora - (inicioDe(e) ?? ahora);
  return [...equipos].sort(
    (a, b) =>
      (orden[a.estado] ?? 9) - (orden[b.estado] ?? 9) ||
      edad(b) - edad(a) ||
      (a.cliente ?? '').localeCompare(b.cliente ?? '') ||
      a.nombre.localeCompare(b.nombre),
  );
}
